import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { invalidationIntents, queryKeys } from "~/api/cache"

import type { SolarDesignVersionsResultDto } from "../dto/solar-design-versions.dto"
import { solarDesignVersionsService } from "../services/solar-design-versions.service"

export type SolarDesignVersionCommand =
  | {
      kind: `activate`
      proposalId: string
      designId: string
      expectedActiveDesignId: string | null
    }
  | {
      kind: `duplicate`
      proposalId: string
      expectedActiveDesignId: string | null
      fromDesignId?: string
    }
  | { kind: `archive`; proposalId: string; designId: string }
  | { kind: `rename`; proposalId: string; designId: string; name: string }

const run = (
  command: SolarDesignVersionCommand,
): Promise<SolarDesignVersionsResultDto> => {
  switch (command.kind) {
    case `duplicate`:
      return solarDesignVersionsService.postDuplicate(command.proposalId, {
        expectedActiveDesignId: command.expectedActiveDesignId,
        ...(command.fromDesignId ? { fromDesignId: command.fromDesignId } : {}),
      })
    case `activate`:
      return solarDesignVersionsService.postActivate(
        command.proposalId,
        command.designId,
        command.expectedActiveDesignId,
      )
    case `rename`:
      return solarDesignVersionsService.patchName(
        command.proposalId,
        command.designId,
        command.name,
      )
    case `archive`:
      return solarDesignVersionsService.postArchive(
        command.proposalId,
        command.designId,
      )
  }
}

/**
 * Duplicate, switch, rename and archive a Vision design version. Each runs
 * inline at the gateway; a duplicate or switch changes the active design, so
 * every read it feeds is invalidated and pricing and savings show out of date.
 * A failure refetches the versions, since a lock or another change may be why.
 */
export default function useSolarDesignVersionCommand(): UseMutationResult<
  SolarDesignVersionsResultDto,
  unknown,
  SolarDesignVersionCommand
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: run,
    onSuccess: (saved, command) => {
      queryClient.setQueryData(
        queryKeys.solarProposal.designVersions(command.proposalId),
        saved.versions,
      )
      if (command.kind === `duplicate` || command.kind === `activate`) {
        return invalidationIntents.solarDesignVersionChanged(
          queryClient,
          command.proposalId,
        )
      }
      // A rename moves the design's version, which the designer saves under.
      return queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.designer(command.proposalId),
        exact: true,
      })
    },
    onError: (_error, { proposalId }) =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.designVersions(proposalId),
        exact: true,
      }),
  })
}
